import { Box, Button, Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import type { DoorVisitCustomSlotFilterOption } from '../useDoorVisitFilterOptions';
import {
  buildFilterChips,
  clearFilterChip,
  clearFilters,
  DATE_RANGE_OPTIONS,
  hasActiveFilters,
  getDoorVisitCustomDateErrors,
  SORT_OPTIONS,
  type DoorVisitDateRangePreset,
  type DoorVisitFilterState,
} from './doorVisitFilters';

export type DoorVisitFilterBarProps = {
  filters: DoorVisitFilterState;
  onChange: (filters: DoorVisitFilterState) => void;
  dateRange: DoorVisitDateRangePreset;
  onDateRangeChange: (dateRange: DoorVisitDateRangePreset) => void;
  customFromDate: string;
  customThroughDate: string;
  onCustomRangeChange: (fromDate: string, throughDate: string) => void;
  calendarTimeZone: string | null;
  canvasserOptions: string[];
  customSlots: DoorVisitCustomSlotFilterOption[];
  resolveCanvasserLabel: (canvasserId: string) => string;
};

const NONE_VALUE = '';

// Keeps a currently-applied value visible in its <Select> even if it's no longer
// in the fetched options list (e.g. a stale URL param, or the canvasser was
// removed) — otherwise MUI renders the Select blank since no MenuItem matches.
const withSelectedOption = (options: string[], selected: string | null): string[] =>
  selected && !options.includes(selected) ? [selected, ...options] : options;

export const DoorVisitFilterBar = ({
  filters,
  onChange,
  dateRange,
  onDateRangeChange,
  customFromDate,
  customThroughDate,
  onCustomRangeChange,
  calendarTimeZone,
  canvasserOptions,
  customSlots,
  resolveCanvasserLabel,
}: DoorVisitFilterBarProps) => {
  const resolveCustomSlotLabel = (name: string): string | undefined =>
    customSlots.find((slot) => slot.name === name)?.label;
  const resolveCustomSlotValueLabel = (name: string, value: string): string | undefined =>
    customSlots.find((slot) => slot.name === name)?.values.find((v) => v.value === value)?.label;
  const chips = buildFilterChips(filters, resolveCustomSlotLabel, resolveCustomSlotValueLabel, resolveCanvasserLabel);
  const canvasserSelectOptions = withSelectedOption(canvasserOptions, filters.canvasserId);
  const customSlotNameSelectOptions = withSelectedOption(
    customSlots.map((slot) => slot.name),
    filters.customSlotName,
  );
  const selectedCustomSlot = customSlots.find((slot) => slot.name === filters.customSlotName);
  const customSlotValueSelectOptions = withSelectedOption(
    (selectedCustomSlot?.values ?? []).map((v) => v.value),
    filters.customSlotValue,
  );
  const customDateErrors = getDoorVisitCustomDateErrors(customFromDate || undefined, customThroughDate || undefined);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }}>
          <InputLabel id="door-visit-date-range-label">Date range</InputLabel>
          <Select
            labelId="door-visit-date-range-label"
            label="Date range"
            value={dateRange}
            onChange={(event: SelectChangeEvent) =>
              onDateRangeChange(event.target.value as DoorVisitFilterState['dateRange'])
            }
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {dateRange === 'custom' && (
          <Stack sx={{ flex: '1 1 416px', minWidth: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 2, minWidth: 0 }}>
              <TextField
                id="door-visit-from-date"
                type="date"
                label="From date"
                value={customFromDate}
                onChange={event => onCustomRangeChange(event.target.value, customThroughDate)}
                error={customDateErrors.fromDate !== null}
                helperText={customDateErrors.fromDate ?? ' '}
                slotProps={{
                  htmlInput: {
                    'aria-invalid': customDateErrors.fromDate !== null,
                    'aria-describedby': 'door-visit-from-date-helper-text door-visit-calendar-timezone',
                  },
                  inputLabel: { shrink: true },
                }}
                sx={{ minWidth: 200, maxWidth: '100%', flex: '1 1 200px' }}
              />
              <TextField
                id="door-visit-through-date"
                type="date"
                label="Through date"
                value={customThroughDate}
                onChange={event => onCustomRangeChange(customFromDate, event.target.value)}
                error={customDateErrors.throughDate !== null}
                helperText={customDateErrors.throughDate ?? ' '}
                slotProps={{
                  htmlInput: {
                    'aria-invalid': customDateErrors.throughDate !== null,
                    'aria-describedby': 'door-visit-through-date-helper-text door-visit-calendar-timezone',
                  },
                  inputLabel: { shrink: true },
                }}
                sx={{ minWidth: 200, maxWidth: '100%', flex: '1 1 200px' }}
              />
            </Stack>
            <FormHelperText id="door-visit-calendar-timezone" sx={{ m: 0, mt: 0.5 }}>
              Dates are inclusive. Calendar timezone: {calendarTimeZone ?? 'not yet available'}.
            </FormHelperText>
          </Stack>
        )}

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }} disabled={canvasserSelectOptions.length === 0}>
          <InputLabel id="door-visit-canvasser-label">Canvasser</InputLabel>
          <Select
            labelId="door-visit-canvasser-label"
            label="Canvasser"
            value={canvasserSelectOptions.length === 0 ? NONE_VALUE : (filters.canvasserId ?? NONE_VALUE)}
            onChange={(event: SelectChangeEvent) =>
              onChange({ ...filters, canvasserId: event.target.value === NONE_VALUE ? null : event.target.value })
            }
          >
            <MenuItem value={NONE_VALUE}>
              {canvasserSelectOptions.length === 0 ? 'None available' : 'All canvassers'}
            </MenuItem>
            {canvasserSelectOptions.map((canvasserId) => (
              <MenuItem key={canvasserId} value={canvasserId}>
                {resolveCanvasserLabel(canvasserId)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }} disabled={customSlotNameSelectOptions.length === 0}>
          <InputLabel id="door-visit-custom-slot-label">Issue Tag</InputLabel>
          <Select
            labelId="door-visit-custom-slot-label"
            label="Issue Tag"
            value={customSlotNameSelectOptions.length === 0 ? NONE_VALUE : (filters.customSlotName ?? NONE_VALUE)}
            onChange={(event: SelectChangeEvent) =>
              onChange({
                ...filters,
                customSlotName: event.target.value === NONE_VALUE ? null : event.target.value,
                customSlotValue: null,
              })
            }
          >
            <MenuItem value={NONE_VALUE}>
              {customSlotNameSelectOptions.length === 0 ? 'None available' : 'All custom slots'}
            </MenuItem>
            {customSlotNameSelectOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {resolveCustomSlotLabel(name) ?? name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="medium"
          sx={{ minWidth: 200, flex: '1 0 0' }}
          disabled={!filters.customSlotName || customSlotValueSelectOptions.length === 0}
        >
          <InputLabel id="door-visit-custom-slot-value-label">Voter Position</InputLabel>
          <Select
            labelId="door-visit-custom-slot-value-label"
            label="Voter Position"
            value={customSlotValueSelectOptions.length === 0 ? NONE_VALUE : (filters.customSlotValue ?? NONE_VALUE)}
            onChange={(event: SelectChangeEvent) =>
              onChange({ ...filters, customSlotValue: event.target.value === NONE_VALUE ? null : event.target.value })
            }
          >
            <MenuItem value={NONE_VALUE}>
              {customSlotValueSelectOptions.length === 0 ? 'None available' : 'All values'}
            </MenuItem>
            {customSlotValueSelectOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {filters.customSlotName ? (resolveCustomSlotValueLabel(filters.customSlotName, value) ?? value) : value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }}>
          <InputLabel id="door-visit-sort-label">Sort</InputLabel>
          <Select
            labelId="door-visit-sort-label"
            label="Sort"
            value={filters.sort}
            onChange={(event: SelectChangeEvent) =>
              onChange({ ...filters, sort: event.target.value as DoorVisitFilterState['sort'] })
            }
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {hasActiveFilters(filters) && (
        <Stack
          direction="row"
          data-testid="active-filter-chips"
          sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 2 }}
        >
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              onDelete={() => onChange(clearFilterChip(filters, chip.key))}
            />
          ))}
          <Button size="small" onClick={() => onChange(clearFilters(filters))}>
            Clear all
          </Button>
        </Stack>
      )}
    </Box>
  );
};
